import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Grid, Typography, LinearProgress, Button, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Chip, Tooltip,
  Divider, Tabs, Tab, TextField, MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import PublicIcon from '@mui/icons-material/Public';
import StorageIcon from '@mui/icons-material/Storage';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  pageVariants, sectionVariants,
} from '../../../animations/variants';
import {
  getComplianceSummary, triggerRefresh, getRefreshJob, getActiveRefreshJob, getConfigStatus,
} from '../../../services/securityhubApi';
import { missingEnableCells } from './configStatus';
import ConfigSetupDialog from './ConfigSetupDialog';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState, PortalSectionHeader } from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { severityColor } from './severity';
import {
  CK, FONT_BODY, statusChipSx, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  sectionCardSx, listCardSx, emptyTextSx, statTileSx, statTileFooterSx, actionRowSx, tileEntranceStyle
} from '../portalStyles';

const SEVERITY_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

const severityIcon = {
  CRITICAL: <ErrorOutlineIcon sx={{ fontSize: 28 }} />,
  HIGH:     <WarningAmberIcon sx={{ fontSize: 28 }} />,
  MEDIUM:   <WarningAmberIcon sx={{ fontSize: 28 }} />,
  LOW:      <InfoOutlinedIcon sx={{ fontSize: 28 }} />,
};

// relativeTime returns a short "2 min ago" style string for a past ISO time.
const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, (Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} h ago`;
  return `${Math.round(diffSec / 86400)} d ago`;
};

export default function ComplianceDashboard({ config, onConfigChange }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshJob, setRefreshJob] = useState(null);
  const [missingRecorders, setMissingRecorders] = useState(0);
  const [configCells, setConfigCells] = useState([]);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [assetTab, setAssetTab] = useState(0);
  const [stdAccount, setStdAccount] = useState('');
  const [stdRegion, setStdRegion] = useState('');
  const [stdRows, setStdRows] = useState(null); // per-standard rows shown (filtered or aggregate)
  const pollRef = useRef(null);

  useEffect(() => {
    let ignore = false;
    getConfigStatus().then((res) => {
      if (ignore) return;
      const recs = res?.data?.recorders || [];
      let linking = [];
      try { linking = JSON.parse(config?.linkingRegions || '[]'); } catch (_) { linking = []; }
      const cells = missingEnableCells(recs, linking);
      setMissingRecorders(cells.length);
      setConfigCells(cells);
    }).catch(() => {});
    return () => { ignore = true; };
  }, [config]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getComplianceSummary();
      setSummary(res?.data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // The Standards-compliance section can be scoped to one account/region combo
  // (its score then matches that account's AWS console). With no filter it shows
  // the aggregate from the just-loaded summary; with a filter it re-fetches the
  // per-standard scores for that combo. Re-runs when the filter or summary change.
  useEffect(() => {
    if (!summary) return undefined;
    if (!stdAccount && !stdRegion) { setStdRows(summary.perStandard || []); return undefined; }
    let ignore = false;
    getComplianceSummary(stdAccount, stdRegion)
      .then((res) => { if (!ignore) setStdRows(res?.data?.perStandard || []); })
      .catch(() => {});
    return () => { ignore = true; };
  }, [summary, stdAccount, stdRegion]);

  // On mount, discover a refresh that is already running server-side (started
  // before a navigation away or a full page reload) and re-attach the progress
  // bar to it — the poll effect below then takes over exactly as if the user
  // had just clicked Refresh. A failed lookup is ignored (never treated as
  // "no job"), and a refresh the user already started this session wins.
  useEffect(() => {
    let ignore = false;
    getActiveRefreshJob()
      .then((res) => {
        const job = res?.data;
        if (!ignore && job && job.status === 'running') {
          setRefreshJob((prev) => prev || job);
        }
      })
      .catch(() => { /* transient — the user can still refresh manually */ });
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    if (!refreshJob || refreshJob.status !== 'running') return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const res = await getRefreshJob(refreshJob.id);
        const next = res?.data;
        setRefreshJob(next);
        if (next && next.status !== 'running') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          await loadSummary();
        }
      } catch (_) { /* transient */ }
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [refreshJob, loadSummary]);

  const onRefresh = async () => {
    try {
      const res = await triggerRefresh();
      setRefreshJob(res?.data);
    } catch (e) {
      setError(e.message);
    }
  };

  const totalFailing = SEVERITY_ORDER.reduce(
    (n, s) => n + (summary?.severityFailingCounts?.[s] ?? 0),
    0
  );

  let linkingRegions = [];
  try { linkingRegions = JSON.parse(config?.linkingRegions || '[]'); } catch (_) { linkingRegions = []; }
  const refreshing = refreshJob?.status === 'running';
  const initialLoading = loading && !summary;
  const lastRefreshText = summary?.lastRefreshAt
    ? relativeTime(summary.lastRefreshAt)
    : 'never';

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      {/* === Header === */}
      <motion.div variants={sectionVariants}>
      <PortalPageHeader
        icon={<SecurityIcon sx={{ fontSize: 24 }} />}
        title="Summary"
        subtitle={(
          <>
            Admin account <b>{config.adminAccountId}</b> • Home region <b>{config.homeRegion}</b>
            {' • '}Last refresh: <b>{lastRefreshText}</b>
            {summary && (
              <> • <b>{totalFailing}</b> failing findings across all severities</>
            )}
            {refreshJob?.status === 'completed' && (
              <> • <span>{refreshJob.findingsFetched} findings fetched</span></>
            )}
          </>
        )}
        action={(
          <Tooltip title={refreshing ? 'Refresh already in progress' : 'Fetch the latest findings from AWS'}>
            <span>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                disabled={refreshing}
                onClick={onRefresh}
              >
                {refreshing ? 'Refreshing…' : 'Refresh findings'}
              </Button>
            </span>
          </Tooltip>
        )}
      />
      </motion.div>

      {refreshing && (
        <motion.div variants={sectionVariants}>
          <OperationProgress percent={refreshJob?.progress} label={refreshJob?.progressLabel || 'Refreshing findings…'} sx={{ mb: 2 }} />
        </motion.div>
      )}

      {config.status === 'partial_failure' && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: '8px' }}
            action={
              <Button size="small" onClick={() => navigate('/securityhub/cspm/enrollment')}>
                Review
              </Button>
            }
          >
            Security Hub setup partially failed. {config.errorMessage}
          </Alert>
        </motion.div>
      )}

      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={loadSummary} />
        </motion.div>
      )}

      {initialLoading && (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
            <PortalLoading label="Loading compliance summary…" />
          </Paper>
        </motion.div>
      )}

      {summary && (
        <>
          {/* === Severity stat cards === */}
          <motion.div variants={sectionVariants}>
            {/* No motion wrapper around the tiles — they animate themselves in
                pure CSS; a motion component here can write an inline opacity
                onto them once its own animation settles. */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {SEVERITY_ORDER.map((sev, i) => {
                  const n = summary.severityFailingCounts?.[sev] ?? 0;
                  const color = severityColor(sev);
                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={sev}>
                      <div style={tileEntranceStyle(i)}>
                        <Paper
                          elevation={0}
                          sx={{
                            ...statTileSx,
                            cursor: 'pointer',
                            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': { borderColor: color, boxShadow: `0 4px 12px ${color}20` },
                          }}
                          onClick={() => navigate(`/securityhub/cspm/findings?severity=${sev}`)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                flexShrink: 0,
                                borderRadius: '10px',
                                backgroundColor: `${color}12`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color,
                              }}
                            >
                              {severityIcon[sev]}
                            </Box>
                            <Typography
                              sx={{
                                fontFamily: FONT_BODY,
                                fontWeight: 700,
                                fontSize: '2.5rem',
                                color,
                                lineHeight: 1,
                              }}
                            >
                              {n}
                            </Typography>
                          </Box>
                          <Box sx={statTileFooterSx}>
                            <Typography
                              sx={{
                                fontFamily: FONT_BODY,
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: CK.navy,
                                mb: 0.5,
                              }}
                            >
                              {sev.charAt(0) + sev.slice(1).toLowerCase()}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: FONT_BODY,
                                fontSize: '0.8rem',
                                color: CK.muted,
                                fontWeight: 500,
                              }}
                            >
                              {n === 1 ? 'failing finding' : 'failing findings'}
                            </Typography>
                          </Box>
                        </Paper>
                      </div>
                    </Grid>
                  );
                })}
              </Grid>
          </motion.div>

          {/* === Standards compliance === */}
          <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
            <PortalSectionHeader
              icon={<SecurityIcon sx={{ fontSize: 20 }} />}
              title="Standards compliance"
              subtitle={stdAccount || stdRegion
                ? `Scores for ${stdAccount || 'all accounts'} · ${stdRegion || 'all linking regions'} — control-based, like the AWS console`
                : 'Scores aggregated across all enrolled accounts and linking regions (control-based)'}
              mb={2}
            />
            <Box sx={{ ...actionRowSx, mb: 2.5 }}>
              <TextField select size="small" label="Account" value={stdAccount}
                onChange={(e) => setStdAccount(e.target.value)} sx={{ minWidth: 0, flex: '1 1 210px', maxWidth: 320 }}>
                <MenuItem value="">All accounts</MenuItem>
                {(summary.perAccount || []).map((a) => (
                  <MenuItem key={a.accountId} value={a.accountId}>
                    {a.accountName ? `${a.accountName} (${a.accountId})` : a.accountId}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Region" value={stdRegion}
                onChange={(e) => setStdRegion(e.target.value)} sx={{ minWidth: 0, flex: '1 1 140px', maxWidth: 220 }}>
                <MenuItem value="">All regions</MenuItem>
                {linkingRegions.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Box>
            {missingRecorders > 0 && (
              <Alert
                severity="warning"
                sx={{ mb: 2, borderRadius: '8px' }}
                action={<Button color="inherit" size="small" onClick={() => setConfigDialogOpen(true)}>Set up AWS Config</Button>}
              >
                AWS Config isn&apos;t recording in {missingRecorders} enrolled account/region pair(s) — the standards can&apos;t evaluate those accounts until it&apos;s set up.
              </Alert>
            )}
            {(stdRows || []).map((std) => {
              const total = std.evaluatedCount ?? (std.passingCount + std.failingCount);
              const pct = std.passingPercent || 0;
              const everythingPasses = total > 0 && std.failingCount === 0;
              const noData = total === 0;
              return (
                <Box
                  key={std.standardId}
                  onClick={() => navigate(`/securityhub/cspm/standards/${encodeURIComponent(std.standardId)}`)}
                  sx={{
                    mb: 2.5,
                    cursor: 'pointer',
                    borderRadius: 1,
                    p: 0.5,
                    mx: -0.5,
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    '&:hover': { backgroundColor: CK.pageBg },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      {everythingPasses && <CheckCircleIcon sx={{ color: CK.green, fontSize: 18, flexShrink: 0 }} />}
                      <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 500, color: CK.navy, wordBreak: 'break-word' }}>
                        {std.displayName}
                      </Typography>
                    </Box>
                    {noData ? (
                      <Chip size="small" label="No data yet" sx={statusChipSx('neutral')} />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                        <b>{std.passingCount}</b> / {total} passing
                        {' • '}
                        <b style={{ color: pct >= 80 ? CK.green : pct >= 50 ? CK.amber : CK.red }}>
                          {pct}%
                        </b>
                      </Typography>
                    )}
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={noData ? 0 : pct}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      backgroundColor: CK.border,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: pct >= 80 ? CK.green : pct >= 50 ? CK.amber : CK.red,
                      },
                    }}
                  />
                </Box>
              );
            })}
            {(!stdRows || stdRows.length === 0) && (
              <Typography sx={emptyTextSx}>
                {stdAccount || stdRegion
                  ? 'No control data for this account and region yet — pick another combination or refresh findings.'
                  : 'No standards enabled yet — enable one from Settings to start scoring this organization.'}
              </Typography>
            )}
            <Divider sx={{ mt: 1, mb: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Standards transition from <code>INCOMPLETE</code> → <code>READY</code> within ~10 minutes of enablement; pass/fail counts appear once AWS finishes evaluating controls.
            </Typography>
          </Paper>
          </motion.div>

          {/* === Findings by region === */}
          <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...listCardSx, mb: 3 }}>
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
              <PortalSectionHeader
                icon={<PublicIcon sx={{ fontSize: 20 }} />}
                title="Findings by region"
                subtitle="Failing findings per linking region"
                mb={0}
              />
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    <TableCell sx={headCellSx}>Region</TableCell>
                    <TableCell sx={headCellSx} align="right">Critical</TableCell>
                    <TableCell sx={headCellSx} align="right">High</TableCell>
                    <TableCell sx={headCellSx} align="right">Medium</TableCell>
                    <TableCell sx={headCellSx} align="right">Low</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(summary.perRegion || []).map((r, i) => (
                    <TableRow key={r.region} hover sx={rowSx(i)}>
                      <TableCell sx={bodyCellSx}>{r.region}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{r.critical}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{r.high}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{r.medium}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{r.low}</TableCell>
                    </TableRow>
                  ))}
                  {(!summary.perRegion || summary.perRegion.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                        <Typography sx={emptyTextSx}>
                          No regional findings yet — refresh findings once AWS has evaluated your accounts.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          </motion.div>

          {/* === Assets with most findings === */}
          <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...listCardSx, mb: 3 }}>
            <Box sx={{ px: 3, pt: 3, pb: 0 }}>
              <PortalSectionHeader
                icon={<StorageIcon sx={{ fontSize: 20 }} />}
                title="Assets with most findings"
                subtitle="Accounts and resources carrying the highest failing-finding counts"
                mb={1.5}
              />
              <Tabs
                value={assetTab}
                onChange={(_, v) => setAssetTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <Tab label="Accounts" /><Tab label="Resources" />
              </Tabs>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    <TableCell sx={headCellSx}>{assetTab === 0 ? 'Account' : 'Resource'}</TableCell>
                    <TableCell sx={headCellSx} align="right">Findings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assetTab === 0 && (summary.perAccount || []).map((a, i) => (
                    <TableRow key={a.accountId} hover sx={{ ...rowSx(i), cursor: 'pointer' }}
                      onClick={() => navigate(`/securityhub/cspm/findings?account=${a.accountId}`)}>
                      <TableCell sx={bodyCellSx}>{a.accountName ? `${a.accountName} (${a.accountId})` : a.accountId}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{a.failingCount}</TableCell>
                    </TableRow>
                  ))}
                  {assetTab === 1 && (summary.topResources || []).map((r, i) => (
                    <TableRow key={r.resourceId} hover sx={rowSx(i)}>
                      <TableCell sx={{ ...bodyCellSx, maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={`${r.resourceId} (${r.resourceType})`}>
                          <span>{r.resourceId} <em>({r.resourceType})</em></span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={bodyCellSx} align="right">{r.count}</TableCell>
                    </TableRow>
                  ))}
                  {assetTab === 0 && (!summary.perAccount || summary.perAccount.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                        <Typography sx={emptyTextSx}>
                          No accounts with findings yet — enroll accounts and refresh findings.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {assetTab === 1 && (!summary.topResources || summary.topResources.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                        <Typography sx={emptyTextSx}>
                          No resources with findings yet — refresh findings to populate this list.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          </motion.div>
        </>
      )}

      <ConfigSetupDialog
        open={configDialogOpen}
        mode="enable"
        cells={configCells}
        onClose={() => setConfigDialogOpen(false)}
        onDone={() => onConfigChange && onConfigChange()}
      />
    </motion.div>
  );
}
